import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  Layers,
  OctagonX,
  Plus,
  Search,
  Wand2,
  X,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { InlineNumber } from '../ui/InlineNumber';
import { toast } from '../../lib/toast';
import { getFlow, postScale } from '../../lib/api';
import { compareServiceNames, splitRoot } from '../../lib/serviceSort';
import type { ServiceInfo } from '../../lib/types';
import type { EscalarScope } from './EscalarModal';

interface Props {
  scope: EscalarScope;
  onClose: () => void;
}

interface Row {
  id: string;
  name: string;
  current: number;
  target: number;
  machines?: number;
}

const MAX_REPLICAS = 50;

function scopeLabel(type: EscalarScope['type']): string {
  if (type === 'queue') return 'Fila';
  if (type === 'service') return 'Serviço';
  if (type === 'reset') return 'Todos os serviços ativos';
  return 'Em massa';
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function EscalarPanel({ scope, onClose }: Props) {
  const isReset = scope.type === 'reset';
  const [rows, setRows] = useState<Row[]>(() =>
    scope.items.map(i => ({
      id: i.id,
      name: i.name,
      current: i.currentReplicas,
      target: i.currentReplicas,
    }))
  );
  const [delta, setDelta] = useState(1);
  const [query, setQuery] = useState('');
  const [openFilas, setOpenFilas] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);

  const { data: flow } = useQuery({
    queryKey: ['flow', scope.cluster],
    queryFn: () => getFlow(scope.cluster),
    enabled: !!scope.cluster,
    // O reset lista o que está ligado AGORA: um retrato de 30s atrás deixaria
    // de fora o serviço escalado há pouco.
    staleTime: isReset ? 0 : 30_000,
    refetchOnMount: isReset ? 'always' : true,
  });

  const [seeded, setSeeded] = useState(!isReset);
  useEffect(() => {
    if (seeded || !flow) return;
    setRows(
      flow.services
        .filter(s => s.replicas > 0)
        .map(s => ({
          id: s.id,
          name: s.name,
          current: s.replicas,
          target: 0,
          machines: s.machines,
        }))
    );
    setSeeded(true);
  }, [flow, seeded]);

  // Fila = raiz exata do nome (#32405): Demandas05 e Demandas012 são cartões
  // distintos mesmo dividindo a stack. A busca casa raiz ou stack, para quem
  // digita o rótulo da stack continuar achando as filas. O cartão só oferece
  // o que ainda não foi adicionado; esvaziou, some.
  // A lista avulsa fica para serviço cuja fila não casou com a busca (ex:
  // "consult-pje" atravessa todas as filas), senão cada serviço apareceria
  // duas vezes: no cartão e abaixo dele.
  const picker = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { filas: [], services: [] as ServiceInfo[] };
    const chosen = new Set(rows.map(r => r.id));
    const all = flow?.services ?? [];

    const byRoot = new Map<string, ServiceInfo[]>();
    const rootMatches = (s: ServiceInfo, root: string): boolean =>
      root.toLowerCase().includes(q) ||
      (!!s.stack && s.stack.toLowerCase().includes(q));
    for (const s of all) {
      const [root] = splitRoot(s.name);
      if (!rootMatches(s, root)) continue;
      const arr = byRoot.get(root) ?? [];
      arr.push(s);
      byRoot.set(root, arr);
    }
    const filas = [...byRoot.entries()]
      .map(([root, svcs]) => ({
        root,
        svcs: svcs
          .filter(s => !chosen.has(s.id))
          .sort((x, y) => compareServiceNames(x.name, y.name)),
      }))
      .filter(f => f.svcs.length > 0)
      .sort((x, y) => compareServiceNames(x.root, y.root))
      .slice(0, 10);

    const services = all
      .filter(
        s =>
          !chosen.has(s.id) &&
          s.name.toLowerCase().includes(q) &&
          !rootMatches(s, splitRoot(s.name)[0])
      )
      .sort((x, y) => compareServiceNames(x.name, y.name))
      .slice(0, 30);

    return { filas, services };
  }, [flow, query, rows]);

  // Opções na ordem da tela, para as setas e o Enter (#32209): cada fila, os
  // serviços dela quando aberta, e por fim os serviços avulsos.
  type PickerOption =
    | { kind: 'fila'; root: string; svcs: ServiceInfo[] }
    | { kind: 'svc'; svc: ServiceInfo; parent?: string };
  const options = useMemo<PickerOption[]>(() => {
    const out: PickerOption[] = [];
    for (const f of picker.filas) {
      out.push({ kind: 'fila', root: f.root, svcs: f.svcs });
      if (openFilas.has(f.root)) {
        for (const s of f.svcs)
          out.push({ kind: 'svc', svc: s, parent: f.root });
      }
    }
    for (const s of picker.services) out.push({ kind: 'svc', svc: s });
    return out;
  }, [picker, openFilas]);
  const [active, setActive] = useState(-1);
  useEffect(() => setActive(-1), [query]);
  useEffect(() => {
    setActive(a => (a >= options.length ? options.length - 1 : a));
  }, [options.length]);
  useEffect(() => {
    if (active < 0) return;
    const el = document.getElementById(`picker-opt-${active}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);
  const optionIndex = (o: PickerOption): number =>
    options.findIndex(x =>
      o.kind === 'fila'
        ? x.kind === 'fila' && x.root === o.root
        : x.kind === 'svc' && x.svc.id === o.svc.id
    );

  const toggleFila = (root: string): void =>
    setOpenFilas(prev => {
      const next = new Set(prev);
      if (next.has(root)) next.delete(root);
      else next.add(root);
      return next;
    });

  const addServices = (svcs: ServiceInfo[]): void =>
    setRows(r => {
      const have = new Set(r.map(x => x.id));
      const additions = svcs
        .filter(s => !have.has(s.id))
        .map(s => ({
          id: s.id,
          name: s.name,
          current: s.replicas,
          target: s.replicas,
        }));
      return additions.length ? [...r, ...additions] : r;
    });

  const choose = (o: PickerOption): void =>
    addServices(o.kind === 'fila' ? o.svcs : [o.svc]);

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setQuery('');
      return;
    }
    if (options.length === 0) return;
    const current = active >= 0 ? options[active] : undefined;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(options.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(current ?? options[0]!);
    } else if (e.key === 'ArrowRight' && current?.kind === 'fila') {
      e.preventDefault();
      if (!openFilas.has(current.root)) toggleFila(current.root);
    } else if (e.key === 'ArrowLeft' && current) {
      const root = current.kind === 'fila' ? current.root : current.parent;
      if (!root || !openFilas.has(root)) return;
      e.preventDefault();
      toggleFila(root);
      const parent = options.find(x => x.kind === 'fila' && x.root === root);
      if (parent) setActive(optionIndex(parent));
    }
  };
  const activeClass = (o: PickerOption): string =>
    optionIndex(o) === active
      ? 'ring-1 ring-inset ring-[var(--fm-accent)] bg-accentSoft'
      : '';
  const optId = (o: PickerOption): string => `picker-opt-${optionIndex(o)}`;

  const removeRow = (id: string): void =>
    setRows(r => r.filter(x => x.id !== id));
  const setTarget = (id: string, v: number): void =>
    setRows(r => r.map(x => (x.id === id ? { ...x, target: v } : x)));
  const applyDeltaToAll = (): void =>
    setRows(r =>
      r.map(x => ({ ...x, target: Math.min(MAX_REPLICAS, x.current + delta) }))
    );
  const zeroAll = (): void => setRows(r => r.map(x => ({ ...x, target: 0 })));
  const clearAll = (): void => setRows([]);

  const changes = rows.filter(r => r.target !== r.current);
  const instances = rows.reduce((sum, r) => sum + (r.machines ?? 0), 0);

  const handleApply = async (): Promise<void> => {
    if (changes.length === 0) {
      toast('Nenhuma alteração para aplicar', { tone: 'warn' });
      return;
    }
    setApplying(true);
    let okCount = 0;
    let failCount = 0;
    for (const item of changes) {
      try {
        await postScale({
          cluster: scope.cluster,
          service: item.name,
          replicas: item.target,
          from: item.current,
        });
        okCount += 1;
      } catch (err) {
        failCount += 1;
        toast(`Falha ao escalar ${item.name}: ${(err as Error).message}`, {
          tone: 'danger',
        });
      }
    }
    setApplying(false);
    if (okCount > 0)
      toast(`${okCount} escalonamento(s) aplicado(s)`, { tone: 'info' });
    if (failCount === 0) onClose();
  };

  return (
    <div
      data-testid="escalar-panel"
      onClick={e => e.stopPropagation()}
      className="absolute top-0 right-0 bottom-0 flex w-full max-w-[520px] flex-col border-l border-border bg-surface shadow-2xl"
    >
      <header className="flex items-center gap-2.5 border-b border-border px-5 py-4">
        {isReset ? (
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-dangerBg text-danger">
            <OctagonX size={15} />
          </div>
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accentSoft text-[var(--fm-accent)]">
            <Wand2 size={15} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-text">
            {isReset ? 'Confirmar escalonamento em massa' : 'Escalar máquinas'}
          </h2>
          <div className="mt-0.5 text-[11px] text-textMute">
            Cluster{' '}
            <span className="font-mono font-semibold">{scope.cluster}</span> ·{' '}
            {scopeLabel(scope.type)} · {rows.length} serviço(s)
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-textMute hover:bg-surfaceAlt"
        >
          <X size={15} />
        </button>
      </header>

      {isReset && seeded && rows.length > 0 && (
        <div
          data-testid="reset-impact"
          className="border-b border-danger/30 bg-dangerBg px-5 py-3 text-[12px] font-semibold text-danger"
        >
          Esta ação irá zerar {plural(rows.length, 'serviço', 'serviços')} e
          interromper{' '}
          {plural(instances, 'instância ativa', 'instâncias ativas')}
        </div>
      )}

      {!isReset && (
        <div className="border-b border-borderSoft px-5 py-3">
          <div className="flex items-center gap-2 rounded-md border border-border bg-bg px-2">
            <Search size={13} className="flex-shrink-0 text-textMute" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              role="combobox"
              aria-expanded={options.length > 0}
              aria-controls="escalar-picker-list"
              aria-autocomplete="list"
              aria-activedescendant={
                active >= 0 && active < options.length
                  ? `picker-opt-${active}`
                  : undefined
              }
              placeholder="Adicionar serviço ou fila… (busca por nome)"
              className="h-7 flex-1 bg-transparent text-[11.5px] text-text outline-none placeholder:text-textMute"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Limpar busca"
                className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-textMute hover:bg-surfaceAlt hover:text-text"
              >
                <X size={12} />
              </button>
            )}
          </div>
          {(picker.filas.length > 0 || picker.services.length > 0) && (
            <ul
              id="escalar-picker-list"
              role="listbox"
              className="mt-1 max-h-64 overflow-auto rounded-md border border-border"
            >
              {picker.filas.map(f => {
                const isOpen = openFilas.has(f.root);
                const filaOpt: PickerOption = {
                  kind: 'fila',
                  root: f.root,
                  svcs: f.svcs,
                };
                const listId = `picker-fila-list-${f.root}`;
                return (
                  <li
                    key={`fila-${f.root}`}
                    data-testid={`picker-fila-card-${f.root}`}
                    className="border-b border-borderSoft last:border-b-0"
                  >
                    <div
                      id={optId(filaOpt)}
                      role="option"
                      aria-selected={optionIndex(filaOpt) === active}
                      className={`flex items-stretch bg-accentSoft/40 ${activeClass(filaOpt)}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFila(f.root)}
                        aria-expanded={isOpen}
                        aria-controls={listId}
                        className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5 text-left hover:bg-accentSoft"
                      >
                        <ChevronRight
                          size={12}
                          className={`flex-shrink-0 text-textMute transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        />
                        <Layers
                          size={12}
                          className="flex-shrink-0 text-[var(--fm-accent)]"
                        />
                        <span
                          data-testid="picker-fila"
                          className="flex-1 truncate text-[11px] font-semibold text-text"
                        >
                          Fila {f.root}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => addServices(f.svcs)}
                        className="my-1 mr-2 flex-shrink-0 rounded bg-[var(--fm-accent)] px-2 text-[10.5px] font-semibold text-white hover:opacity-90"
                      >
                        Selecionar todos
                      </button>
                    </div>
                    {isOpen && (
                      <ul id={listId}>
                        {f.svcs.map(s => {
                          const o: PickerOption = {
                            kind: 'svc',
                            svc: s,
                            parent: f.root,
                          };
                          return (
                            <li
                              key={s.id}
                              id={optId(o)}
                              role="option"
                              aria-selected={optionIndex(o) === active}
                            >
                              <button
                                type="button"
                                onClick={() => addServices([s])}
                                className={`flex w-full items-center gap-2 py-1.5 pl-9 pr-2.5 text-left hover:bg-surfaceAlt ${activeClass(o)}`}
                              >
                                <Plus
                                  size={12}
                                  className="flex-shrink-0 text-textMute"
                                />
                                <span
                                  data-testid="picker-fila-service"
                                  className="flex-1 truncate font-mono text-[11px] text-text"
                                >
                                  {s.name}
                                </span>
                                <span className="font-mono text-[10px] text-textMute">
                                  {s.system} · {s.replicas}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
              {picker.services.map(s => {
                const o: PickerOption = { kind: 'svc', svc: s };
                return (
                  <li
                    key={s.id}
                    id={optId(o)}
                    role="option"
                    aria-selected={optionIndex(o) === active}
                  >
                    <button
                      type="button"
                      onClick={() => addServices([s])}
                      className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-surfaceAlt ${activeClass(o)}`}
                    >
                      <Plus size={12} className="flex-shrink-0 text-textMute" />
                      <span
                        data-testid="picker-service"
                        className="flex-1 truncate font-mono text-[11px] text-text"
                      >
                        {s.name}
                      </span>
                      <span className="font-mono text-[10px] text-textMute">
                        {s.system} · {s.replicas}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {!isReset && rows.length > 0 && (
        <div className="flex items-center gap-2 border-b border-borderSoft bg-surfaceAlt px-5 py-2.5">
          <span className="text-[10px] font-bold uppercase tracking-[.06em] text-textMute">
            Ação em massa
          </span>
          <span className="text-[10.5px] text-textMute">+</span>
          <InlineNumber
            value={delta}
            onChange={setDelta}
            min={0}
            max={MAX_REPLICAS}
          />
          <Button size="sm" tone="primary" onClick={applyDeltaToAll}>
            Aplicar a todos
          </Button>
          <Button size="sm" tone="danger" onClick={zeroAll}>
            Zerar todos
          </Button>
          <div className="flex-1" />
          <Button size="sm" onClick={clearAll}>
            Limpar
          </Button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isReset && !seeded ? (
          <div className="px-5 py-10 text-center text-[12px] text-textMute">
            Carregando serviços ativos…
          </div>
        ) : isReset && rows.length === 0 ? (
          <div className="px-5 py-10 text-center text-[12px] text-textMute">
            Nenhum serviço ativo para zerar neste cluster.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-10 text-center text-[12px] text-textMute">
            Busque acima e adicione os serviços (ou uma fila inteira) que deseja
            escalar.
          </div>
        ) : (
          <ul className="divide-y divide-borderSoft">
            {rows.map(item => (
              <li
                key={item.id}
                data-testid={
                  isReset ? `reset-row-${item.id}` : `escalar-row-${item.id}`
                }
                className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-5 py-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate font-mono text-[11.5px] font-semibold text-text">
                    {item.name}
                  </div>
                  <div className="mt-0.5 text-[10.5px] text-textMute">
                    atual: <span className="font-mono">{item.current}</span>
                  </div>
                </div>
                {isReset ? (
                  <span className="font-mono text-[11.5px] font-semibold text-danger">
                    {item.current} → 0
                  </span>
                ) : (
                  <InlineNumber
                    value={item.target}
                    onChange={v => setTarget(item.id, v)}
                    min={0}
                    max={MAX_REPLICAS}
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeRow(item.id)}
                  aria-label={`remover ${item.name}`}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-textMute hover:bg-surfaceAlt hover:text-text"
                >
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <footer className="flex items-center gap-2 border-t border-border bg-surfaceAlt px-5 py-3">
        <Button size="sm" onClick={onClose} disabled={applying}>
          Cancelar
        </Button>
        <div className="flex-1" />
        {isReset ? (
          <Button
            size="sm"
            tone="danger"
            onClick={handleApply}
            disabled={applying || changes.length === 0}
          >
            Confirmar e aplicar
          </Button>
        ) : (
          <Button
            size="sm"
            tone="primary"
            onClick={handleApply}
            disabled={applying || changes.length === 0}
          >
            Aplicar ({changes.length} altera
            {changes.length === 1 ? 'ção' : 'ções'})
          </Button>
        )}
      </footer>
    </div>
  );
}

EscalarPanel.displayName = 'EscalarPanel';

export default EscalarPanel;
